import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../supabaseClient";
import "./CSS/TopBar.css";
import UserAvatar from "../Assets/user-avatar.svg";
import ArrowDown from "../Assets/arrow-down.svg";
import Avatar1 from "../Assets/Avatars/avatar1.png";
import Avatar2 from "../Assets/Avatars/avatar2.png";
import Avatar3 from "../Assets/Avatars/avatar3.png";
import Avatar4 from "../Assets/Avatars/avatar4.png";
import Avatar5 from "../Assets/Avatars/avatar5.png";
import Avatar6 from "../Assets/Avatars/avatar6.png";
import { lightHaptic } from "../native/capacitor";
import {
  clearPasskeyForUser,
  enrollLocalPasskey,
  getLastPasskeyError,
  getPasskeySupportIssue,
  isPasskeyEnabledForUser,
  isPasskeySupported,
  setPasskeyEnabledForUser,
  verifyLocalPasskey,
} from "../auth/passkey";

const TopBar = ({ profile, setShowNameModal, setProfile }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const avatarOptions = [UserAvatar, Avatar1, Avatar2, Avatar3, Avatar4, Avatar5, Avatar6];

  const handleSignOut = async () => {
    await lightHaptic();
    setDropdownOpen(false);
    await supabase.auth.signOut();
    navigate("/login");
  };

  const handleSwitchToAdmin = () => {
    lightHaptic();
    setDropdownOpen(false);
    navigate("/admin");
  };

  const userName = profile?.name || "Guest User";
  const userEmail = profile?.email || "No Email Found";
  const isPreservedPath = (url = "") =>
    url.startsWith("/static") || url.startsWith("data:") || url.includes("Assets/");
  const avatarSrc = profile?.avatar_url
    ? isPreservedPath(profile.avatar_url)
      ? profile.avatar_url
      : `${process.env.REACT_APP_API_BASE_URL}/api/image/${encodeURIComponent(profile.avatar_url)}`
    : UserAvatar;

  const handlePresetAvatar = async (avatar) => {
    if (!profile?.id) return;
    await lightHaptic();
    const { error } = await supabase
      .from("profiles")
      .update({ avatar_url: avatar })
      .eq("id", profile.id);
    if (error) {
      console.error("Failed to update avatar:", error.message);
      return;
    }
    setProfile?.({ ...profile, avatar_url: avatar });
    setShowAvatarPicker(false);
  };

  const handleAvatarUpload = async (file) => {
    if (!file || !profile?.id) return;
    await lightHaptic();
    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append("avatar", file);
    formData.append("userId", profile.id);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/upload-avatar`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error("Avatar upload failed");
      const { objectKey } = await res.json();
      setProfile?.({ ...profile, avatar_url: objectKey });
      setShowAvatarPicker(false);
    } catch (err) {
      console.error("Avatar upload failed:", err);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleTogglePasskey = async () => {
    if (!profile?.id) return;
    await lightHaptic();

    if (!isPasskeySupported()) {
      alert("Face ID/Touch ID unlock is only available in the mobile app.");
      return;
    }
    const supportIssue = await getPasskeySupportIssue();
    if (supportIssue) {
      alert(supportIssue);
      return;
    }

    if (passkeyEnabled) {
      const ok = await verifyLocalPasskey({ userId: profile.id });
      if (!ok) return;
      clearPasskeyForUser(profile.id);
      setPasskeyEnabled(false);
      return;
    }

    const enrolled = await enrollLocalPasskey({
      userId: profile.id,
      userName: profile?.email || profile?.name || profile.id,
    });
    if (enrolled) {
      setPasskeyEnabledForUser(profile.id, true);
      setPasskeyEnabled(true);
    } else {
      alert(getLastPasskeyError() || "Could not enable Face ID/Touch ID unlock.");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setShowAvatarPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!profile?.id) return;
    setPasskeyEnabled(isPasskeyEnabledForUser(profile.id));
  }, [profile?.id]);

  return (
    <div className="topbar-container">
      <div className="profile-dropdown-container" ref={dropdownRef}>
        <div
          className={`profile-section ${dropdownOpen ? "open" : ""}`}
          onClick={() => {
            lightHaptic();
            setDropdownOpen(!dropdownOpen);
          }}
        >
          <div className="user-name">{userName}</div>
          <img src={avatarSrc} alt="User Avatar" className="user-avatar" />
          <img
            src={ArrowDown}
            alt="Arrow Down"
            className={`arrow-down ${dropdownOpen ? "open" : ""}`}
          />
        </div>
        <div className={`dropdown-menu ${dropdownOpen ? "open" : ""}`}>
          <div className="dropdown-item email-text">{userEmail}</div>
          <button
            className="dropdown-item"
            onClick={() => {
              setShowNameModal(true);
              setDropdownOpen(false);
              setShowAvatarPicker(false);
            }}
          >
            Edit Profile
          </button>
          <button
            className="dropdown-item"
            onClick={() => setShowAvatarPicker((prev) => !prev)}
          >
            Edit Avatar
          </button>
          <button className="dropdown-item" onClick={handleTogglePasskey}>
            {passkeyEnabled ? "Disable Face ID / Touch ID" : "Enable Face ID / Touch ID"}
          </button>
          {showAvatarPicker && (
            <div className="topbar-avatar-picker">
              <div className="topbar-avatar-grid">
                {avatarOptions.map((avatar, idx) => (
                  <img
                    key={idx}
                    src={avatar}
                    alt={`Avatar ${idx + 1}`}
                    className="topbar-avatar-choice"
                    onClick={() => handlePresetAvatar(avatar)}
                  />
                ))}
                <button
                  className="topbar-avatar-choice topbar-upload-avatar-btn"
                  onClick={() => fileRef.current?.click()}
                  aria-label="Upload avatar"
                  type="button"
                >
                  +
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(event) => handleAvatarUpload(event.target.files?.[0])}
                />
              </div>
              {uploadingAvatar && <div className="topbar-avatar-uploading">Uploading...</div>}
            </div>
          )}
          <button className="dropdown-item" onClick={handleSignOut}>
            Sign Out
          </button>
          {profile?.is_admin && (
            <button className="dropdown-item" onClick={handleSwitchToAdmin}>
              Switch to Admin
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopBar;
